import { useState, useEffect, useCallback, useRef } from "react";
import Cookies from "js-cookie";
import { getApi } from "../../../../utils/api.ts";
import { GET_WFH_TIMESHEETS, API_GET_TIMESHEET_STATUS } from "../../../../../config/Endpoints.js";
import { TimesheetEntry } from "../types.ts";
import type { ProjectAllocationRow } from "../utils/timesheet-allocation.ts";
import {
  resolveGroupApprovalStatus,
  type TimesheetApproval,
} from "../utils/resolve-group-approval.ts";
import { v4 as uuidv4 } from "uuid";

interface ApiEntry {
  entryId?: string | null;
  id?: number;
  titleType: string;
  projectId: number | null;
  titleName: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  phase?: TimesheetEntry["phase"];
  description: string;
  duration: number;
  taskStatus: string;
  projectName: string | null;
  editable?: boolean;
  isAutoApproved?: boolean | string;
  isAutoapprovde?: boolean | string;
  approvalStatus?: string;
  approverStatus?: string;
  workStream?: string;
  workStreamId?: number | null;
}

interface ApiTimesheet {
  id: number | null;
  employeeId?: number;
  date: string;
  isSubmitted: number | null;
  entries: ApiEntry[];
  approvals?: TimesheetApproval[];
  type?: string;
  attemptsRemaining?: number | null;
  timesheetFound?: boolean;
  allowedTill?: string | null;
  hrStatus?: string | null;
}

export interface TimesheetSheetMeta {
  type: string;
  attemptsRemaining: number;
  timesheetFound: boolean;
  employeeId?: number;
  allowedTill?: string | null;
  hrStatus?: string | null;
}

export interface TimesheetStatusInfo {
  workDate: string;
  allowedTill: string;
  status: string;
  message: string;
  projectAllocations?: ProjectAllocationRow[];
  filled: boolean;
  expired: boolean;
}

export function useTimesheetEntries(selectedDate: Date) {
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [statusInfo, setStatusInfo] = useState<TimesheetStatusInfo | null>(null);
  const [sheetMeta, setSheetMeta] = useState<TimesheetSheetMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const accessToken = Cookies.get("accessToken");

  const fetchTimesheetData = useCallback(async (isRefetch = false) => {
    const formattedDate = selectedDate.toLocaleDateString("en-CA");
    if (isRefetch) setIsRefreshing(true);
    else setLoading(true);
    try {
      const statusResponse = await getApi(
        `${API_GET_TIMESHEET_STATUS}?date=${formattedDate}`,
        accessToken,
      );

      if (statusResponse.data) {
        setStatusInfo(statusResponse.data);
      }
    } catch {
      /* status is supplementary */
    }

    try {
      const response = await getApi<ApiTimesheet[]>(
        `${GET_WFH_TIMESHEETS}?date=${formattedDate}`,
        accessToken,
      );

      const draft = response.data?.[0];

      if (draft) {
        setSheetMeta({
          type: draft.type ?? "REGULAR",
          attemptsRemaining: draft.attemptsRemaining ?? 0,
          timesheetFound: draft.timesheetFound ?? true,
          employeeId: draft.employeeId,
          allowedTill: draft.allowedTill ?? null,
          hrStatus: draft.hrStatus ?? null,
        });

        setDraftId(draft.id ?? null);
        setIsSubmitted(draft.isSubmitted === 1);

        if (draft.entries?.length) {
          const mapped = draft.entries.map((entry) => ({
            id: entry.entryId || uuidv4(),
            projectId: entry.projectId || undefined,
            project:
              entry.titleType === "PROJECT"
                ? entry.projectName || `Project-${entry.projectId}`
                : "Miscellaneous",
            category: entry.titleName || "",
            categoryId: entry.categoryId ?? null,
            phase: entry.phase ?? null,
            phaseId: entry.phaseId ?? null,
            phaseCode: entry.phaseCode ?? null,
            phaseName: entry.phaseName ?? null,
            description: entry.description,
            hours: String(Math.floor(entry.duration / 60)),
            minutes: String(entry.duration % 60).padStart(2, "0"),
            titleType: entry.titleType as "PROJECT" | "OTHERS",
            status:
              entry.taskStatus?.toLowerCase() === "inprogress"
                ? "in-progress"
                : entry.taskStatus?.toLowerCase() || "",
            date: formattedDate,
            workStream: entry.workStream || undefined,
            workStreamId: entry.workStreamId ?? null,
            editable: entry.editable,
            isAutoApproved: entry.isAutoApproved,
            isAutoapprovde: entry.isAutoapprovde,
            approvalStatus:
              resolveGroupApprovalStatus(
                draft.approvals,
                entry.projectId ?? null,
                entry.titleType === "OTHERS",
              ) ??
              entry.approvalStatus ??
              entry.approverStatus,
          }));

          setEntries(mapped);
        } else {
          setEntries([]);
        }
      } else {
        setDraftId(null);
        setIsSubmitted(false);
        setEntries([]);
        setSheetMeta(null);
      }
    } catch {
      setDraftId(null);
      setIsSubmitted(false);
      setSheetMeta(null);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
      hasLoadedOnceRef.current = true;
    }
  }, [selectedDate, accessToken]);

  const refetch = useCallback(
    () => fetchTimesheetData(true),
    [fetchTimesheetData],
  );

  useEffect(() => {
    setEntries([]);
    setSheetMeta(null);
    hasLoadedOnceRef.current = false;
  }, [selectedDate]);

  useEffect(() => {
    void fetchTimesheetData(false);
  }, [fetchTimesheetData]);

  return {
    entries,
    setEntries,
    draftId,
    setDraftId,
    isSubmitted,
    setIsSubmitted,
    statusInfo,
    sheetMeta,
    loading,
    isRefreshing,
    refetch,
  };
}
